import React, { useState } from 'react'

import {
    Box,
    Button,
    CircularProgress,
    ListSubheader,
    Menu,
    MenuItem,
    Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { ConcessionMergeVariable } from '../../models/Concession'
import { useConcessionMergeVariables } from '../../hooks/useConcessionMessages'

const useStyles = makeStyles((theme: Theme) => ({
    insertButton: {
        padding: '4px 8px',
        fontSize: '0.75rem',
        textTransform: 'none',
        color: '#6b7280',
        borderColor: '#d1d5db',
        borderRadius: 4,
        minWidth: 'auto',
        '&:hover': {
            backgroundColor: '#f1f5f9',
            borderColor: '#008C85',
            color: '#008C85',
        },
    },
    subheader: {
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#9ca3af',
        lineHeight: '32px',
        backgroundColor: '#ffffff',
    },
    menuItem: {
        fontSize: '0.8rem',
        padding: theme.spacing(0.75, 2),
    },
    variableKey: {
        fontFamily: 'monospace',
        fontSize: '0.7rem',
        color: '#6b7280',
        marginLeft: theme.spacing(1),
    },
}))

const CATEGORY_LABELS: Record<string, string> = {
    tenant: 'Tenant',
    property: 'Property',
    concession: 'Concession',
}

interface Props {
    onSelect: (variableTag: string) => void
}

export const ConcessionMergeVariableDropdown: React.FC<Props> = ({
    onSelect,
}) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const {
        mergeVariables,
        isLoadingMergeVariables,
    } = useConcessionMergeVariables()

    const handleSelect = (variable: ConcessionMergeVariable) => {
        onSelect(`{{${variable.key}}}`)
        setAnchorEl(null)
    }

    const grouped = mergeVariables.reduce<
        Record<string, ConcessionMergeVariable[]>
    >((acc, variable) => {
        if (!acc[variable.category]) acc[variable.category] = []
        acc[variable.category].push(variable)
        return acc
    }, {})

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setAnchorEl(e.currentTarget)
                }}
                className={classes.insertButton}
            >
                {'{{ }}'} Insert Variable
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ style: { maxHeight: 320, minWidth: 240 } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                getContentAnchorEl={null}
            >
                {isLoadingMergeVariables ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={20} />
                    </Box>
                ) : mergeVariables.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="textSecondary">
                            No variables available
                        </Typography>
                    </MenuItem>
                ) : (
                    Object.entries(grouped).map(([category, variables]) => [
                        <ListSubheader
                            key={`header-${category}`}
                            className={classes.subheader}
                        >
                            {CATEGORY_LABELS[category] || category}
                        </ListSubheader>,
                        ...variables.map((variable) => (
                            <MenuItem
                                key={variable.key}
                                onClick={() => handleSelect(variable)}
                                className={classes.menuItem}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                >
                                    <Typography variant="body2">
                                        {variable.label}
                                    </Typography>
                                    <Typography className={classes.variableKey}>
                                        {`{{${variable.key}}}`}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        )),
                    ])
                )}
            </Menu>
        </>
    )
}
